import { Button, Empty, Table, Tag } from "@arco-design/web-react";
import type { ColumnProps } from "@arco-design/web-react/es/Table";

import { canUsePermission } from "../../../lib/auth";
import { useCurrentAuthUser } from "../../auth";
import { FINANCE_PAYABLE_LIST_EXPORT_PERMISSION } from "../config";
import { useFinancePayableOverviewRuntime } from "../runtime/useFinanceOverviewListRuntime";
import { financePayableRequestStatusLabel, formatFinancePayableAmount } from "../service";
import type { FinancePayableOverviewRow, FinancePayableRequestStatus } from "../types";
import { FinanceOverviewExportFeedback, FinanceOverviewListFeedback, FinanceOverviewTotals } from "./FinanceOverviewListParts";
import { FinancePayableOverviewFilterCard } from "./FinancePayableOverviewFilterCard";
import "../../../shared/ui/crmListPageLayout.css";
import "./financeOverview.css";

type Props = {
  /** 点「详情」进这个团的应付清单。 */
  onOpenDetail: (itineraryId: string) => void;
};

const dash = <span className="finance-overview-muted">—</span>;

/** 请款状态标签的颜色：多请待退醒目，已结清绿色。 */
const REQUEST_STATUS_COLORS: Readonly<Record<FinancePayableRequestStatus, string>> = {
  awaiting_request: "arcoblue",
  in_progress: "orange",
  settled: "green",
  over_requested: "red",
};

/** 金额列：¥ 加千分位两位小数，负数带负号（未付付超了是负数，与应付清单一致）。 */
function money(title: string, pick: (row: FinancePayableOverviewRow) => string): ColumnProps<FinancePayableOverviewRow> {
  return {
    title,
    width: 130,
    align: "right",
    render: (_, row) => <span className="finance-overview-money">{formatFinancePayableAmount(pick(row))}</span>,
  };
}

/**
 * 应付列表：有确认件或有请款的行程一行（正常就是一个团），四个金额与这个团的应付清单同一个数。
 * 几个订单指向同一行程时团编码都列出来，没有团编码的显示订单号；换过行程、没有订单指向的只显示线路。
 * 请款状态由后端按每家结算主体判好；没有任何应付数据的行不显示状态。
 * 合计是全部筛选结果；「详情」按接口给的能不能点显示。
 */
export function FinancePayableOverviewFeaturePage({ onOpenDetail }: Props) {
  const runtime = useFinancePayableOverviewRuntime();
  const data = runtime.data;
  const canExport = canUsePermission(useCurrentAuthUser().data, FINANCE_PAYABLE_LIST_EXPORT_PERMISSION);
  const columns: ColumnProps<FinancePayableOverviewRow>[] = [
    {
      title: "团编码",
      width: 180,
      className: "finance-overview-code-column",
      render: (_, row) =>
        row.orders.length ? (
          <div className="finance-overview-codes">
            {row.orders.map((order) => (
              <span key={order.orderId}>{order.teamId ?? order.orderNo}</span>
            ))}
          </div>
        ) : (
          dash
        ),
    },
    { title: "线路", render: (_, row) => row.routeName ?? dash },
    { title: "出团日期", width: 110, render: (_, row) => row.departureDate ?? dash },
    { title: "计调", width: 140, render: (_, row) => (row.dispatcherNames.length ? row.dispatcherNames.join("、") : dash) },
    { title: "结算主体家数", dataIndex: "settlementSupplierCount", width: 110, align: "right" },
    money("真实应付", (row) => row.itineraryPayableTotal),
    money("已请款", (row) => row.requestedTotal),
    money("已付", (row) => row.paidTotal),
    money("未付", (row) => row.unpaidTotal),
    {
      title: "请款状态",
      width: 110,
      render: (_, row) =>
        row.requestStatus ? (
          <Tag color={REQUEST_STATUS_COLORS[row.requestStatus]}>{financePayableRequestStatusLabel(row.requestStatus)}</Tag>
        ) : (
          dash
        ),
    },
    ...(data?.canViewDetail
      ? [
          {
            title: "操作",
            width: 80,
            render: (_: unknown, row: FinancePayableOverviewRow) => (
              <Button type="text" onClick={() => onOpenDetail(row.itineraryId)}>详情</Button>
            ),
          },
        ]
      : []),
  ];
  return (
    <main className="finance-overview-page crm-list-page" aria-label="应付列表">
      <FinancePayableOverviewFilterCard
        filters={runtime.draft}
        onChange={runtime.setDraft}
        onSearch={runtime.search}
        onReset={runtime.reset}
      />
      <section className="crm-list-panel" aria-busy={runtime.loading}>
        <div className="crm-list-section-header">
          <div className="crm-list-title-row">
            <h2>应付列表</h2>
            <div className="crm-list-statline">共 {data?.total ?? 0} 个行程</div>
          </div>
          {canExport ? (
            <div className="crm-list-table-actions">
              <Button loading={runtime.exporting} onClick={() => void runtime.exportExcel()}>
                导出 Excel
              </Button>
            </div>
          ) : null}
        </div>
        <p className="finance-overview-note">花儿内部成本、团总成本暂不显示：要等内部成本的录入方式定下来。</p>
        <p className="finance-overview-note">
          「多请待退」只表示有结算主体的已请款超过了它的当前应付，不代表已经多付、要退款或已经在追回。
        </p>
        <FinanceOverviewExportFeedback listName="应付列表" error={runtime.exportError} />
        <FinanceOverviewListFeedback
          listName="应付列表"
          limitExceeded={runtime.limitExceeded}
          failed={runtime.failed}
          onRetry={runtime.refresh}
        />
        {data ? (
          <FinanceOverviewTotals
            label="应付列表合计"
            items={[
              { name: "行程数", value: String(data.totals.itineraryCount) },
              { name: "真实应付合计", value: formatFinancePayableAmount(data.totals.itineraryPayableTotal) },
              { name: "已请款合计", value: formatFinancePayableAmount(data.totals.requestedTotal) },
              { name: "已付合计", value: formatFinancePayableAmount(data.totals.paidTotal) },
              { name: "未付合计", value: formatFinancePayableAmount(data.totals.unpaidTotal) },
            ]}
          />
        ) : null}
        <Table<FinancePayableOverviewRow>
          rowKey="itineraryId"
          loading={runtime.loading}
          columns={columns}
          data={data?.items ?? []}
          scroll={{ x: 1520 }}
          noDataElement={<Empty description={runtime.limitExceeded ? "请加出团日期范围后再查" : "没有符合条件的行程"} />}
          pagination={{
            current: runtime.page,
            pageSize: runtime.pageSize,
            total: data?.total ?? 0,
            onChange: runtime.setPage,
          }}
        />
      </section>
    </main>
  );
}
